import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useCMS } from '../../context/CMSContext';
import { 
  LayoutDashboard, FileText, Star, Package, DollarSign, 
  BookOpen, LogOut, Menu, X, Users, Mail, Bell, ChevronRight,
  Home, Settings, Image, Info, Briefcase, HelpCircle, BarChart3, Calendar, UserCheck, Share2,
  Calculator, Sparkles, Play, Scale, Search, FlaskConical, Gift, ShieldCheck, Video
} from 'lucide-react';

const AdminLayout = () => {
  const { isAdmin, loading, logout, getDashboardStats, hasPermission, isSuperAdmin, adminUser } = useCMS();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate('/admin/login');
    }
  }, [isAdmin, loading, navigate]);

  useEffect(() => {
    if (isAdmin) {
      getDashboardStats().then(setStats).catch(console.error);
    }
  }, [isAdmin, getDashboardStats]);

  if (loading) {
    return (
      <div className="min-h-screen bg-page-bg flex items-center justify-center">
        <div className="text-text-secondary">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) return null;

  // Menu items with permission keys
  const allMenuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', permission: 'dashboard' },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics', permission: 'analytics' },
    { path: '/admin/crm', icon: UserCheck, label: 'CRM', permission: 'crm' },
    { path: '/admin/bookings', icon: Calendar, label: 'Bookings', permission: 'bookings' },
    { path: '/admin/booking-settings', icon: Video, label: 'Booking Settings', permission: 'bookings' },
    { path: '/admin/hero', icon: Home, label: 'Hero Section', permission: 'cms_hero' },
    { path: '/admin/testimonials', icon: Star, label: 'Testimonials', permission: 'cms_testimonials' },
    { path: '/admin/features', icon: Package, label: 'Features', permission: 'cms_features' },
    { path: '/admin/features-pricing', icon: DollarSign, label: 'Features & Pricing', permission: 'cms_features' },
    { path: '/admin/pricing', icon: DollarSign, label: 'Pricing (Legacy)', permission: 'cms_features' },
    { path: '/admin/case-studies', icon: BookOpen, label: 'Case Studies', permission: 'cms_case_studies' },
    { path: '/admin/about', icon: Info, label: 'About Page', permission: 'cms_about' },
    { path: '/admin/solutions', icon: Briefcase, label: 'Solutions', permission: 'cms_solutions' },
    { path: '/admin/faqs', icon: HelpCircle, label: 'FAQs', permission: 'cms_faqs' },
    { path: '/admin/roi-settings', icon: Calculator, label: 'ROI Calculator', permission: 'cms_roi' },
    { path: '/admin/contact-settings', icon: Mail, label: 'Contact Page', permission: 'cms_contact' },
    { path: '/admin/ai-demo-settings', icon: Play, label: 'AI Demo Page', permission: 'cms_ai_demo' },
    { path: '/admin/ai-features-settings', icon: Sparkles, label: 'AI Features Page', permission: 'cms_ai_demo' },
    { path: '/admin/legal-pages', icon: Scale, label: 'Legal Pages', permission: 'cms_legal' },
    { path: '/admin/seo-settings', icon: Search, label: 'SEO Settings', permission: 'cms_seo' },
    { path: '/admin/ab-testing', icon: FlaskConical, label: 'A/B Testing', permission: 'ab_testing' },
    { path: '/admin/promo-popup', icon: Gift, label: 'Promo Popup', permission: 'cms_promo_popup' },
    { path: '/admin/social-links', icon: Share2, label: 'Social Links', permission: 'cms_seo' },
    { path: '/admin/images', icon: Image, label: 'Image Library', permission: 'cms_hero' },
    { path: '/admin/sections', icon: FileText, label: 'Page Sections', permission: 'cms_hero' },
    { path: '/admin/users', icon: ShieldCheck, label: 'User Management', permission: 'user_management', superAdminOnly: true },
  ];

  // Filter menu items based on permissions
  const menuItems = allMenuItems.filter(item => {
    if (item.superAdminOnly && !isSuperAdmin()) return false;
    return hasPermission(item.permission, 'view');
  });

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const isActivePath = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-page-bg flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-primary-navy text-white
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/getcare360-logo.png" 
                alt="GetCare 360" 
                className="h-8 w-auto"
              />
              <span className="text-xs bg-tech-blue/20 text-tech-blue px-2 py-1 rounded">CMS</span>
            </Link>
          </div>

          {/* Menu */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActivePath(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center space-x-3 px-4 py-3 rounded-lg transition-all
                    ${active 
                      ? 'bg-tech-blue text-white' 
                      : 'text-white/70 hover:bg-white/10 hover:text-white'}
                  `}
                >
                  <Icon size={20} />
                  <span className="font-medium">{item.label}</span>
                  {active && <ChevronRight className="ml-auto" size={16} />}
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-white/10">
            <Link
              to="/"
              className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-white transition-colors"
            >
              <Home size={20} />
              <span>View Website</span>
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 text-red-400 hover:text-red-300 transition-colors"
            >
              <LogOut size={20} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="bg-surface border-b border-border-line px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-text-secondary hover:text-text-primary"
            >
              <Menu size={24} />
            </button>

            <div className="flex items-center space-x-4 ml-auto">
              {stats && (
                <div className="hidden md:flex items-center space-x-4 text-sm">
                  <div className="flex items-center space-x-2 text-text-secondary">
                    <Users size={16} />
                    <span>{stats.stats?.demo_requests || 0} leads</span>
                  </div>
                  <div className="flex items-center space-x-2 text-text-secondary">
                    <Mail size={16} />
                    <span>{stats.stats?.contacts || 0} messages</span>
                  </div>
                </div>
              )}
              
              {/* Current user info */}
              {adminUser && (
                <div className="flex items-center gap-2 pl-4 border-l border-border-line">
                  <div className="text-right hidden sm:block">
                    <div className="text-sm font-medium text-text-primary">{adminUser.name}</div>
                    <div className="text-xs text-text-secondary">
                      {adminUser.role === 'super_admin' ? 'Super Admin' : 'Sub Admin'}
                    </div>
                  </div>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    adminUser.role === 'super_admin' ? 'bg-heritage-orange/10' : 'bg-tech-blue/10'
                  }`}>
                    <ShieldCheck size={16} className={
                      adminUser.role === 'super_admin' ? 'text-heritage-orange' : 'text-tech-blue'
                    } />
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

// Dashboard home component
export const AdminDashboard = () => {
  const { getDashboardStats } = useCMS();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDashboardStats()
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getDashboardStats]);

  if (loading) {
    return <div className="text-text-secondary">Loading dashboard...</div>;
  }

  const statCards = [
    { label: 'Demo Requests', value: stats?.stats?.demo_requests || 0, icon: Users, color: 'bg-tech-blue' },
    { label: 'Contact Messages', value: stats?.stats?.contacts || 0, icon: Mail, color: 'bg-care-teal' },
    { label: 'Newsletter Subs', value: stats?.stats?.newsletter_subscribers || 0, icon: Bell, color: 'bg-heritage-orange' },
    { label: 'Feature Unlocks', value: stats?.stats?.feature_unlocks || 0, icon: Package, color: 'bg-purple-500' },
    { label: 'Case Studies', value: stats?.stats?.total_blog_posts || 0, icon: BookOpen, color: 'bg-pink-500' },
    { label: 'Published', value: stats?.stats?.published_posts || 0, icon: FileText, color: 'bg-green-500' },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-text-primary">Dashboard</h1>
        <p className="text-text-secondary mt-1">Welcome to your CMS dashboard</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="border-border-line">
              <CardContent className="p-4">
                <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mb-3`}>
                  <Icon className="text-white" size={20} />
                </div>
                <div className="text-2xl font-bold text-text-primary">{stat.value}</div>
                <div className="text-sm text-text-secondary">{stat.label}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Demos */}
        <Card className="border-border-line">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Recent Demo Requests</h3>
            <div className="space-y-3">
              {stats?.recent_demos?.length > 0 ? (
                stats.recent_demos.map((demo, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-page-bg rounded-lg">
                    <div>
                      <div className="font-medium text-text-primary">{demo.name}</div>
                      <div className="text-sm text-text-secondary">{demo.company}</div>
                    </div>
                    <div className="text-sm text-text-secondary">{demo.staffSize} staff</div>
                  </div>
                ))
              ) : (
                <p className="text-text-secondary text-sm">No demo requests yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Contacts */}
        <Card className="border-border-line">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Recent Messages</h3>
            <div className="space-y-3">
              {stats?.recent_contacts?.length > 0 ? (
                stats.recent_contacts.map((contact, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-page-bg rounded-lg">
                    <div>
                      <div className="font-medium text-text-primary">{contact.name}</div>
                      <div className="text-sm text-text-secondary truncate max-w-[200px]">{contact.subject}</div>
                    </div>
                    <div className={`text-xs px-2 py-1 rounded ${contact.status === 'unread' ? 'bg-tech-blue/10 text-tech-blue' : 'bg-gray-100 text-gray-500'}`}>
                      {contact.status}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-text-secondary text-sm">No messages yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Links */}
      <Card className="border-border-line">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/admin/hero" className="p-4 bg-page-bg rounded-lg hover:bg-soft-sky transition-colors text-center">
              <Home className="mx-auto mb-2 text-tech-blue" size={24} />
              <span className="text-sm font-medium text-text-primary">Edit Hero</span>
            </Link>
            <Link to="/admin/case-studies" className="p-4 bg-page-bg rounded-lg hover:bg-soft-sky transition-colors text-center">
              <BookOpen className="mx-auto mb-2 text-tech-blue" size={24} />
              <span className="text-sm font-medium text-text-primary">New Case Study</span>
            </Link>
            <Link to="/admin/testimonials" className="p-4 bg-page-bg rounded-lg hover:bg-soft-sky transition-colors text-center">
              <Star className="mx-auto mb-2 text-tech-blue" size={24} />
              <span className="text-sm font-medium text-text-primary">Add Testimonial</span>
            </Link>
            <Link to="/admin/features" className="p-4 bg-page-bg rounded-lg hover:bg-soft-sky transition-colors text-center">
              <Package className="mx-auto mb-2 text-tech-blue" size={24} />
              <span className="text-sm font-medium text-text-primary">Manage Features</span>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLayout;
